import { usePermissions } from '@/hooks/usePermissions';
import { getScheduledEvent } from '@/utils/stateAccessors';
import { Button, Modal } from '@payaca/ambrosia';
import { TaggableEntityType } from '@payaca/types/tagTypes';
import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import * as dealActions from '@payaca/store/deals/dealsActions';
import * as notesActions from '@payaca/store/notes/notesActions';
import * as scheduledEventActions from '@payaca/store/scheduledEvents/scheduledEventsActions';
import * as taskActions from '@payaca/store/tasks/tasksActions';
import * as uploadsActions from '@payaca/store/uploads/uploadsActions';

import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { UploadsPermissions } from '@payaca/permissions/uploads/uploads.permissions';

import EntityTagControl from '../entityTagControl/EntityTagControl';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ScheduledEventDetailsAndActionsHeader from '../scheduledEventDetailsAndActionsHeader/ScheduledEventDetailsAndActionsHeader';
import ScheduledEventKeyInformationHeader from '../scheduledEventKeyInformationHeader/ScheduledEventKeyInformationHeader';

import useGetScheduledEvent from '@/api/queries/scheduleEvents/useGetScheduledEvent';
import { useSelector } from '@/api/state';
import LinkProjectFiles from '@/ui/components/scheduledEventReadView/LinkProjectFiles';
import GoogleMapAnchor from '@payaca/components/googleMapAnchor/GoogleMapAnchor';
import Lightbox from '@payaca/components/lightbox/Lightbox';
import { getAcceptedFileTypes } from '@payaca/helpers/fileHelper';
import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import moment from 'moment-timezone';
import CreateTimelogControl from '../createTimelogControl/CreateTimelogControl';
import EntityTimelogsDrawer from '../entityTimelogsDrawer/EntityTimelogsDrawer';
import { ScheduledEventFileUploadModal } from '../scheduledEventFileUploadModal/ScheduledEventFileUploadModal';
import UploadCollectionControl from '../uploadCollectionControl/UploadCollectionControl';
import { ContactsSection } from './ContactsSection';
import FolderEntryItem from './FolderEntryItem';
import { NotesSection } from './NotesSection';
import './ScheduledEventReadView.sass';
import {
  SectionBodyBlock,
  SectionDivider,
  SectionTitle,
} from './SectionComponents';
import { TasksSection } from './TasksSection';

const acceptFileTypes = getAcceptedFileTypes(['document', 'image', 'video']);

type Props = {
  scheduledEventId: number;
  hideDealLink?: boolean;
  hideNotes?: boolean;
  onDeleteScheduledEventSuccess?: () => void;
  onUpdateScheduledEventSuccess?: () => void;
  onUpdateTasksSuccess?: () => void;
};
const ScheduledEventReadView: FC<Props> = ({
  scheduledEventId,
  hideDealLink,
  hideNotes,
  onDeleteScheduledEventSuccess,
  onUpdateScheduledEventSuccess,
  onUpdateTasksSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();

  const { userHasRequiredPermission } = usePermissions();
  const profile = useSelector((state: any) => state.users.myProfile);

  const scheduledEvent = useSelector((state) =>
    getScheduledEvent(state, scheduledEventId)
  );

  const { scheduledEvent: scheduledEventWithFolderEntries } =
    useGetScheduledEvent(scheduledEventId).data || {};

  const [showCreateTimelogModal, setShowCreateTimelogModal] = useState(false);
  const [showTimelogsDrawer, setShowTimelogsDrawer] = useState(false);
  const [showLinkFilesModal, setShowLinkFilesModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] =
    useState<boolean>(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    dispatch(scheduledEventActions.requestGetScheduledEvent(scheduledEventId));
    dispatch(taskActions.requestGetTasksForScheduledEvent(scheduledEventId));
    dispatch(
      uploadsActions.requestGetUploadsForEntity(
        scheduledEventId,
        'scheduledEvent'
      )
    );
    {
      if (!hideNotes) {
        dispatch(
          notesActions.getNotesForScheduledEvent.request({
            scheduledEventId,
            callback: () => {
              dispatch(
                scheduledEventActions.requestGetScheduledEvent(scheduledEventId)
              );
            },
          })
        );
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [hideNotes, scheduledEventId]);

  useEffect(() => {
    if (scheduledEvent?.dealId) {
      dispatch(dealActions.requestGetDeal(scheduledEvent.dealId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [scheduledEvent?.dealId]);

  const { userCanUpdateEvent, userCanTagEvent } = useMemo(
    () => ({
      userCanUpdateEvent: userHasRequiredPermission([
        ScheduledEventsPermissions.UPDATE_EVENT,
      ]),
      userCanTagEvent: userHasRequiredPermission([
        ScheduledEventsPermissions.TAG_EVENT,
      ]),
    }),
    [userHasRequiredPermission]
  );

  const userCanDeleteAttachment = useMemo(() => {
    return userHasRequiredPermission([UploadsPermissions.DELETE_UPLOAD]);
  }, [userHasRequiredPermission]);

  const onPersistUploadSuccess = useCallback(() => {
    dispatch(scheduledEventActions.requestGetScheduledEvent(scheduledEventId));
    dispatch(
      uploadsActions.requestGetUploadsForEntity(
        scheduledEventId,
        'scheduledEvent'
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [scheduledEventId]);

  const removeUpload = (uploadId: number) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(
        uploadsActions.unlinkUpload.request({
          uploadId,
          entity: {
            type: 'scheduledEvent',
            id: scheduledEventId,
          },
          deleteUploadIfOrphaned: true,
          callback: () => {
            dispatch(
              scheduledEventActions.requestGetScheduledEvent(scheduledEventId)
            );
            dispatch(
              uploadsActions.requestGetUploadsForEntity(
                scheduledEventId,
                'scheduledEvent'
              )
            );
            resolve();
          },
          onErrorCallback: () => {
            reject();
          },
        })
      );
    });
  };

  const addressAsString = scheduledEvent?.location?.address
    ? getAddressAsString(scheduledEvent.location.address)
    : undefined;

  const initialFormState = useMemo(() => {
    if (!scheduledEvent) return {};
    return {
      primaryLinkedEntity: {
        entityId: scheduledEventId,
        entityType: 'scheduledEvent' as const,
      },
      startTime: moment(scheduledEvent.beginAt).isBefore(moment())
        ? scheduledEvent.beginAt
        : undefined,
    };
  }, [scheduledEventId, scheduledEvent]);

  if (!scheduledEvent) return null;

  // document files linked to the event
  const folderEntries = scheduledEventWithFolderEntries?.folderEntries ?? [];

  const hasNextSlide = lightboxIndex + 1 < folderEntries.length;
  const hasPrevSlide = lightboxIndex - 1 >= 0;

  return (
    <div className={'scheduled-event-read-view'}>
      <ScheduledEventDetailsAndActionsHeader
        scheduledEventId={scheduledEventId}
        hideDealLink={hideDealLink}
        onDeleteScheduledEventSuccess={onDeleteScheduledEventSuccess}
        onUpdateScheduledEventSuccess={onUpdateScheduledEventSuccess}
      />
      {(userCanUpdateEvent ||
        userCanTagEvent ||
        !!scheduledEvent.tags?.length) && (
        <EntityTagControl
          entityId={scheduledEvent.id}
          entityType={TaggableEntityType.SCHEDULED_EVENT}
          entityTags={scheduledEvent.tags || []}
          onEntityTagsChange={() =>
            dispatch(
              scheduledEventActions.requestGetScheduledEvent(scheduledEvent.id)
            )
          }
          isReadOnly={!userCanUpdateEvent && !userCanTagEvent}
        />
      )}
      <ScheduledEventKeyInformationHeader scheduledEvent={scheduledEvent} />
      {scheduledEvent.customerName && (
        <div>
          <SectionTitle>Customer</SectionTitle>
          <SectionDivider />
          <SectionBodyBlock>{scheduledEvent.customerName}</SectionBodyBlock>
        </div>
      )}
      {scheduledEvent.location?.id && (
        <div>
          <SectionTitle>Location</SectionTitle>
          <SectionDivider />
          <SectionBodyBlock>
            <GoogleMapAnchor
              addressText={addressAsString}
              coordinates={scheduledEvent.location.address}
            >
              {addressAsString}
            </GoogleMapAnchor>
          </SectionBodyBlock>
        </div>
      )}
      <ContactsSection scheduledEventId={scheduledEventId} />
      <PermissionGuard
        renderIfHasPermissions={[
          TasksPermissions.GET_TASKS,
          TasksPermissions.GET_SELF_ASSIGNED_TASKS,
        ]}
      >
        <TasksSection
          scheduledEventId={scheduledEventId}
          onUpdateTasksSuccess={() => {
            onUpdateTasksSuccess?.();
            dispatch(
              scheduledEventActions.requestGetScheduledEvent(scheduledEvent.id)
            );
          }}
        />
      </PermissionGuard>
      <PermissionGuard
        renderIfHasPermissions={[
          TimelogsPermissions.GET_SELF_ASSIGNED_TIMELOGS,
          TimelogsPermissions.GET_TIMELOGS,
        ]}
      >
        <div>
          <SectionTitle>Timelogs</SectionTitle>
          <SectionDivider />
          <div className="flex flex-row gap-4">
            <Button onClick={() => setShowTimelogsDrawer(true)} variant="link">
              View timelogs
            </Button>
            <EntityTimelogsDrawer
              isOpen={showTimelogsDrawer}
              onClose={() => setShowTimelogsDrawer(false)}
              entity={{
                entityType: 'scheduledEvent',
                entityId: scheduledEventId,
                entityReadableIdentifier: scheduledEvent.name,
              }}
              newTimelogsInitialStartTime={
                moment(scheduledEvent.beginAt).isBefore(moment())
                  ? scheduledEvent.beginAt
                  : undefined
              }
            />
            <PermissionGuard
              renderIfHasPermissions={[
                TimelogsPermissions.CREATE_TIMELOGS,
                TimelogsPermissions.CREATE_SELF_ASSIGNED_TIMELOGS,
              ]}
            >
              <>
                <Button
                  className="record-timelog-control"
                  variant="link"
                  onClick={() => setShowCreateTimelogModal(true)}
                >
                  Record timelog
                </Button>
                <Modal
                  title="Record a timelog"
                  isOpen={showCreateTimelogModal}
                  onClose={() => setShowCreateTimelogModal(false)}
                >
                  <CreateTimelogControl
                    initialFormState={initialFormState}
                    onCreateTimelogSuccess={() => {
                      setShowCreateTimelogModal(false);
                    }}
                  />
                </Modal>
              </>
            </PermissionGuard>
          </div>
        </div>
      </PermissionGuard>
      {scheduledEvent.description && (
        <div>
          <SectionTitle>Description</SectionTitle>
          <SectionDivider />
          <SectionBodyBlock>
            <div className="whitespace-pre-wrap">
              {scheduledEvent.description}
            </div>
          </SectionBodyBlock>
        </div>
      )}
      <PermissionGuard
        renderIfHasPermissions={[UploadsPermissions.GET_UPLOADS]}
      >
        <div>
          <SectionTitle>Attachments</SectionTitle>
          <SectionDivider />
          <div className="mb-4 flex flex-row gap-4">
            <Button variant="link" onClick={() => setShowFileUploadModal(true)}>
              Add new files
            </Button>
            {scheduledEvent.dealId && (
              <PermissionGuard
                renderIfHasPermissions={[DealsPermissions.GET_DEAL]}
              >
                <Button
                  variant="link"
                  onClick={() => setShowLinkFilesModal(true)}
                >
                  Link project files
                </Button>
              </PermissionGuard>
            )}
          </div>

          <UploadCollectionControl
            uploadIds={scheduledEvent.uploadIds || []}
            onUploadCollectionChange={onPersistUploadSuccess}
            allowMultipleUploads={true}
            canRemove={userCanDeleteAttachment}
            canAdd={false}
            acceptFileTypes={acceptFileTypes}
            customRemoveUpload={removeUpload}
          />

          {folderEntries.length > 0 && (
            // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width
            <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-4 flex-wrap">
              {folderEntries.map((folderEntry, index) => (
                <FolderEntryItem
                  key={folderEntry.id}
                  folderEntry={folderEntry}
                  scheduledEventId={scheduledEventWithFolderEntries!.publicId}
                  onClick={() => {
                    setLightboxIndex(index);
                    setLightboxOpen(true);
                  }}
                />
              ))}
            </div>
          )}

          <Lightbox
            isOpen={
              folderEntries.length > 0 &&
              folderEntries[lightboxIndex] &&
              lightboxOpen
            }
            onClose={() => setLightboxOpen(false)}
            onNext={
              hasNextSlide
                ? () => setLightboxIndex((prev) => prev + 1)
                : undefined
            }
            onPrev={
              hasPrevSlide
                ? () => setLightboxIndex((prev) => prev - 1)
                : undefined
            }
            lightboxDetail={{
              type: 'shadowDOM',
              title: folderEntries[lightboxIndex]?.name,
              subtitle: null,
              html:
                folderEntries[lightboxIndex]?.__typename ===
                'DocumentInstanceFolderEntry'
                  ? folderEntries[lightboxIndex].documentInstance?.documentHtml
                  : '',
            }}
          />
        </div>
      </PermissionGuard>
      {!hideNotes && (
        <NotesSection
          scheduledEventId={scheduledEventId}
          onUpdateNotesSuccess={() => {
            dispatch(
              scheduledEventActions.requestGetScheduledEvent(scheduledEvent.id)
            );
          }}
        />
      )}
      {scheduledEvent?.dealId && (
        <LinkProjectFiles
          disabledFiles={{
            uploadIds: scheduledEvent.uploadIds.map((x) => x.toString()),
            folderEntryIds:
              scheduledEventWithFolderEntries?.folderEntries.map((x) => x.id) ||
              [],
          }}
          isOpen={showLinkFilesModal}
          onClose={() => {
            setShowLinkFilesModal(false);
          }}
          projectNumericalId={scheduledEvent.dealId}
          targetEventId={scheduledEventId}
          onLinkFilesSuccess={() => {
            setShowLinkFilesModal(false);
            dispatch(
              scheduledEventActions.requestGetScheduledEvent(scheduledEventId)
            );
            dispatch(
              uploadsActions.requestGetUploadsForEntity(
                scheduledEventId,
                'scheduledEvent'
              )
            );
          }}
        />
      )}
      <ScheduledEventFileUploadModal
        isOpen={showFileUploadModal}
        onClose={() => setShowFileUploadModal(false)}
        scheduledEventId={scheduledEventId}
        onUploadSuccess={() => {
          setShowFileUploadModal(false);
          dispatch(
            scheduledEventActions.requestGetScheduledEvent(scheduledEventId)
          );
          dispatch(
            uploadsActions.requestGetUploadsForEntity(
              scheduledEventId,
              'scheduledEvent'
            )
          );
        }}
      />
    </div>
  );
};
export default ScheduledEventReadView;
